import { getStorage, ref, uploadBytesResumable } from 'firebase/storage';
import { v4 as uuidv4 } from 'uuid';
import { app } from '../firebase';

const DEFAULT_FOLDER = 'product_images';
const IMAGE_PROCESSING_DEFAULTS = {
  maxWidth: 1920,
  maxHeight: 1920,
  convertToWebp: true,
  webpQuality: 0.82,
};

const storage = getStorage(app);

const isBrowserEnv = () =>
  typeof window !== 'undefined' &&
  typeof document !== 'undefined' &&
  typeof HTMLCanvasElement !== 'undefined';

const isFirebaseStorageUrl = (value) =>
  typeof value === 'string' &&
  (value.includes('firebasestorage.googleapis.com') ||
    value.includes('storage.googleapis.com'));

const extractFilenameFromUrl = (url) => {
  if (!url) return null;

  const [pathWithoutQuery] = url.split('?');
  const decodedPath = decodeURIComponent(pathWithoutQuery);
  const segments = decodedPath.split('/');
  return segments.pop() || null;
};

const unwrapImageSource = (value) => {
  if (!value || typeof value !== 'object') {
    return value;
  }

  if (typeof File !== 'undefined' && value instanceof File) {
    return value;
  }

  if (value.original) {
    const original = unwrapImageSource(value.original);
    if (original) {
      return original;
    }
  }

  if ('src' in value) {
    return unwrapImageSource(value.src);
  }

  if (typeof value.name === 'string' && value.name.trim().length > 0) {
    return value.name;
  }

  return value;
};

const normalizeImageInput = (input) => {
  const value = unwrapImageSource(input);

  if (!value) {
    return { filename: null, file: null, shouldUpload: false };
  }

  if (typeof File !== 'undefined' && value instanceof File) {
    return { filename: null, file: value, shouldUpload: true };
  }

  if (typeof value === 'string') {
    if (isFirebaseStorageUrl(value)) {
      return {
        filename: extractFilenameFromUrl(value),
        file: null,
        shouldUpload: false,
      };
    }
    return { filename: value, file: null, shouldUpload: false };
  }

  if (value && typeof value === 'object' && typeof value.name === 'string') {
    return { filename: value.name, file: null, shouldUpload: false };
  }

  return { filename: null, file: null, shouldUpload: false };
};

const sanitizeFileName = (name) => {
  if (!name) return 'image';
  return name
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9._-]/g, '-')
    .replace(/-+/g, '-');
};

const ensureExtension = (name, extension) => {
  const base = name.replace(/\.[^/.]+$/, '');
  return `${base}.${extension}`;
};

const canvasToBlob = (canvas, mimeType, quality) =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Failed to convert canvas to Blob'));
        }
      },
      mimeType,
      quality
    );
  });

const loadImageElement = (file) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(img);
    };

    img.onerror = (error) => {
      URL.revokeObjectURL(objectUrl);
      reject(error);
    };

    img.src = objectUrl;
  });

const preprocessImageFile = async (file, options = {}) => {
  if (
    !isBrowserEnv() ||
    !file ||
    typeof file.type !== 'string' ||
    !file.type.startsWith('image/')
  ) {
    return file;
  }

  const { maxWidth, maxHeight, convertToWebp, webpQuality } = {
    ...IMAGE_PROCESSING_DEFAULTS,
    ...options,
  };

  const image = await loadImageElement(file);

  const widthRatio =
    typeof maxWidth === 'number' && maxWidth > 0
      ? maxWidth / image.width
      : 1;
  const heightRatio =
    typeof maxHeight === 'number' && maxHeight > 0
      ? maxHeight / image.height
      : 1;

  const ratio = Math.min(1, widthRatio, heightRatio);
  const targetWidth = Math.max(1, Math.round(image.width * ratio));
  const targetHeight = Math.max(1, Math.round(image.height * ratio));

  let canvas = document.createElement('canvas');
  canvas.width = targetWidth;
  canvas.height = targetHeight;

  const context = canvas.getContext('2d', { alpha: false });
  if (!context) return file;

  context.drawImage(image, 0, 0, targetWidth, targetHeight);

  const mimeType = convertToWebp ? 'image/webp' : file.type;
  const blob = await canvasToBlob(
    canvas,
    mimeType,
    convertToWebp ? webpQuality : undefined
  );

  const finalName = sanitizeFileName(
    convertToWebp ? ensureExtension(file.name, 'webp') : file.name
  );

  if (typeof File === 'function') {
    return new File([blob], finalName, {
      type: mimeType,
      lastModified: Date.now(),
    });
  }

  const processedFile = blob;
  processedFile.name = finalName;
  return processedFile;
};

const buildStorageObjectName = (file) => {
  const originalName =
    (file && typeof file.name === 'string' && file.name) || 'image.webp';
  const sanitizedName = sanitizeFileName(originalName);
  return `${uuidv4()}-${sanitizedName}`;
};

const uploadFileToFolder = (file, folder, onProgress) =>
  new Promise((resolve, reject) => {
    const storageObjectName = buildStorageObjectName(file);
    const storageRef = ref(storage, `${folder}/${storageObjectName}`);
    const uploadTask = uploadBytesResumable(storageRef, file);

    uploadTask.on(
      'state_changed',
      (snapshot) => {
        if (typeof onProgress === 'function') {
          const progress =
            snapshot.totalBytes > 0
              ? (snapshot.bytesTransferred / snapshot.totalBytes) * 100
              : 0;
          onProgress({
            progress,
            snapshot,
            fileName: storageObjectName,
          });
        }
      },
      reject,
      () => resolve(storageObjectName)
    );
  });

export const uploadImageToFirebase = async (
  image,
  {
    folder = DEFAULT_FOLDER,
    onProgress,
    maxWidth,
    maxHeight,
    convertToWebp,
    webpQuality,
  } = {}
) => {
  const { filename, file, shouldUpload } = normalizeImageInput(image);

  if (shouldUpload && file) {
    const processedFile = await preprocessImageFile(file, {
      maxWidth,
      maxHeight,
      convertToWebp,
      webpQuality,
    });
    return uploadFileToFolder(processedFile, folder, onProgress);
  }

  return filename;
};

export const uploadManyImagesToFirebase = async (
  images,
  {
    folder = DEFAULT_FOLDER,
    onProgress,
    maxWidth,
    maxHeight,
    convertToWebp,
    webpQuality,
  } = {}
) => {
  if (!Array.isArray(images) || images.length === 0) {
    return [];
  }

  const progressCallbacks =
    typeof onProgress === 'function'
      ? images.map((image, index) => (payload) =>
          onProgress({ ...payload, index })
        )
      : null;

  return Promise.all(
    images.map((image, index) =>
      uploadImageToFirebase(image, {
        folder,
        onProgress: progressCallbacks ? progressCallbacks[index] : undefined,
        maxWidth,
        maxHeight,
        convertToWebp,
        webpQuality,
      })
    )
  );
};

export const getFirebaseFilename = (imageLike) =>
  normalizeImageInput(imageLike).filename;

export const FirebaseStorage = {
  uploadImageToFirebase,
  uploadManyImagesToFirebase,
  getFirebaseFilename,
};

export const IMAGE_DEFAULTS = {
  folder: DEFAULT_FOLDER,
  ...IMAGE_PROCESSING_DEFAULTS,
};
